/**
 * SharedListStore - Event-driven state management for item lists
 *
 * Manages a list of items (cart, shortlist, comparison) with:
 * - Event emission for UI updates
 * - Persistence to session flowState via API
 * - Running totals and item counts
 *
 * Usage:
 * ```typescript
 * const store = new SharedListStore();
 * store.on('change', (items) => updateUI(items));
 * store.addItem({ id: '1', title: 'BMW M4', price: 62995 });
 * ```
 */

export interface ListItem {
  id: string;
  type: 'product' | 'vehicle' | 'property' | 'generic' | 'build' | 'player' | 'service';
  title: string;
  subtitle?: string;
  price?: number;
  currency?: string;
  image?: string;
  category?: string;  // e.g., "runes", "items", "players", "properties"
  tags?: string[];    // e.g., ["core", "mythic", "recommended"]
  metadata?: Record<string, unknown>;
  addedAt: number;
}

/**
 * Context types for smart suggestions
 */
export type ListContext =
  | 'shopping'      // E-commerce cart
  | 'comparison'    // Comparing items
  | 'build'         // Game builds, configurations
  | 'shortlist'     // Favorites, saved items
  | 'booking'       // Properties, vehicles, services
  | 'research';     // General research/collection

/**
 * Detected context with confidence
 */
export interface DetectedContext {
  type: ListContext;
  confidence: number;
  suggestedActions: ContextAction[];
  summary: string;
}

export interface ContextAction {
  label: string;
  value: string;
  icon?: string;
  variant?: 'primary' | 'secondary';
}

export interface ListState {
  items: ListItem[];
  createdAt: number;
  lastModified: number;
}

export type ListEvent = 'change' | 'add' | 'remove' | 'clear';

export type ListEventHandler = (items: ListItem[], item?: ListItem) => void;

/** Max emit events per second before throttling */
const MAX_EMITS_PER_SECOND = 20;

export class SharedListStore {
  private items: ListItem[] = [];
  private listeners: Map<ListEvent, Set<ListEventHandler>> = new Map();
  private createdAt: number = Date.now();
  private lastModified: number = Date.now();

  // Safety: throttle rapid emits to prevent infinite loops
  private emitCount: number = 0;
  private emitResetTimeout: ReturnType<typeof setTimeout> | null = null;
  private isThrottled: boolean = false;

  constructor() {
    // Initialize listener sets
    this.listeners.set('change', new Set());
    this.listeners.set('add', new Set());
    this.listeners.set('remove', new Set());
    this.listeners.set('clear', new Set());
  }

  /**
   * Subscribe to list events.
   */
  on(event: ListEvent, handler: ListEventHandler): () => void {
    this.listeners.get(event)?.add(handler);
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from list events.
   */
  off(event: ListEvent, handler: ListEventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }

  /**
   * Emit an event to all listeners.
   * Protected by throttling to prevent infinite loops.
   */
  private emit(event: ListEvent, item?: ListItem): void {
    // Safety: throttle rapid emits
    if (this.isThrottled) {
      console.warn(`[ListStore] Throttled - ignoring ${event} emit`);
      return;
    }

    this.emitCount++;
    if (this.emitCount > MAX_EMITS_PER_SECOND) {
      console.error('[ListStore] Too many emits per second - throttling');
      this.isThrottled = true;
      // Auto-reset after 2 seconds
      setTimeout(() => {
        this.isThrottled = false;
        this.emitCount = 0;
      }, 2000);
      return;
    }

    // Reset counter after 1 second of no emits
    if (this.emitResetTimeout) {
      clearTimeout(this.emitResetTimeout);
    }
    this.emitResetTimeout = setTimeout(() => {
      this.emitCount = 0;
    }, 1000);

    this.listeners.get(event)?.forEach((handler) => {
      try {
        handler([...this.items], item);
      } catch (error) {
        console.error(`[ListStore] Error in ${event} handler:`, error);
      }
    });
  }

  /**
   * Add an item to the list.
   */
  addItem(item: Omit<ListItem, 'addedAt'>): ListItem {
    const newItem: ListItem = {
      ...item,
      addedAt: Date.now(),
    };

    // Check for duplicates by ID
    const existingIndex = this.items.findIndex((i) => i.id === item.id);
    if (existingIndex >= 0) {
      console.log(`[ListStore] Item ${item.id} already in list, skipping`);
      return this.items[existingIndex]!;
    }

    this.items.push(newItem);
    this.lastModified = Date.now();

    console.log(`[ListStore] Added item: ${item.title} (${this.items.length} total)`);

    this.emit('add', newItem);
    this.emit('change', newItem);

    return newItem;
  }

  /**
   * Remove an item from the list by ID.
   */
  removeItem(itemId: string): boolean {
    const index = this.items.findIndex((i) => i.id === itemId);
    if (index < 0) {
      console.log(`[ListStore] Item ${itemId} not found`);
      return false;
    }

    const removed = this.items.splice(index, 1)[0]!;
    this.lastModified = Date.now();

    console.log(`[ListStore] Removed item: ${removed.title} (${this.items.length} remaining)`);

    this.emit('remove', removed);
    this.emit('change', removed);

    return true;
  }

  /**
   * Clear all items from the list.
   */
  clear(): void {
    if (this.items.length === 0) return;

    this.items = [];
    this.lastModified = Date.now();

    console.log('[ListStore] Cleared all items');

    this.emit('clear');
    this.emit('change');
  }

  /**
   * Get all items.
   */
  getItems(): ListItem[] {
    return [...this.items];
  }

  /**
   * Get item count.
   */
  getCount(): number {
    return this.items.length;
  }

  /**
   * Check if an item is in the list.
   */
  hasItem(itemId: string): boolean {
    return this.items.some((i) => i.id === itemId);
  }

  /**
   * Calculate total price of all items.
   */
  getTotal(): { amount: number; currency: string } {
    const total = this.items.reduce((sum, item) => sum + (item.price || 0), 0);
    // Use first item's currency or default to GBP
    const currency = this.items.find((i) => i.currency)?.currency || 'GBP';
    return { amount: total, currency };
  }

  /**
   * Get formatted total string.
   */
  getFormattedTotal(): string {
    const { amount, currency } = this.getTotal();
    if (amount === 0) return '';

    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return formatter.format(amount);
  }

  /**
   * Get list state for API persistence.
   */
  getState(): ListState {
    return {
      items: [...this.items],
      createdAt: this.createdAt,
      lastModified: this.lastModified,
    };
  }

  /**
   * Restore list state from API.
   */
  setState(state: ListState): void {
    this.items = [...state.items];
    this.createdAt = state.createdAt;
    this.lastModified = state.lastModified;

    console.log(`[ListStore] Restored ${this.items.length} items from state`);

    this.emit('change');
  }

  /**
   * Generate summary text for AI context.
   */
  getSummaryForAI(): string {
    if (this.items.length === 0) {
      return '';
    }

    const total = this.getFormattedTotal();
    const itemsList = this.items
      .map((item) => {
        const price = item.price
          ? ` (${new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: item.currency || 'GBP',
              minimumFractionDigits: 0,
            }).format(item.price)})`
          : '';
        return `- ${item.title}${price}`;
      })
      .join('\n');

    return `[CURRENT LIST]
${this.items.length} item${this.items.length > 1 ? 's' : ''}${total ? ` totaling ${total}` : ''}:
${itemsList}

User may reference these items or ask to compare/book/purchase.`;
  }

  /**
   * Detect the context/intent of the collected items.
   * Returns suggested actions based on what's been added.
   */
  detectContext(): DetectedContext {
    if (this.items.length === 0) {
      return {
        type: 'research',
        confidence: 0,
        suggestedActions: [],
        summary: 'No items collected yet',
      };
    }

    // Analyze item types and categories
    const types = this.items.map(i => i.type);
    const categories = this.items.map(i => i.category).filter(Boolean) as string[];
    const hasPrice = this.items.some(i => i.price && i.price > 0);
    const uniqueCategories = [...new Set(categories)];

    // Scoring for different contexts
    let context: ListContext = 'research';
    let confidence = 0.5;
    let suggestedActions: ContextAction[] = [];
    let summary = '';

    // Build context (gaming, configurations)
    const buildKeywords = ['item', 'rune', 'skill', 'build', 'loadout', 'gear', 'equipment'];
    const isBuild = categories.some(c => buildKeywords.some(k => c?.toLowerCase().includes(k))) ||
                    types.includes('build');

    if (isBuild) {
      context = 'build';
      confidence = 0.85;
      summary = `${this.items.length} items in your build`;
      suggestedActions = [
        { label: 'View Build Summary', value: 'build_summary', variant: 'primary' },
        { label: 'Find Alternatives', value: 'search_alternatives' },
        { label: 'Export Build', value: 'export_build' },
      ];
      if (this.items.length >= 2) {
        suggestedActions.unshift({ label: 'Analyze Synergies', value: 'analyze_synergies', variant: 'primary' });
      }
    }
    // Shopping context
    else if (hasPrice && types.includes('product')) {
      context = 'shopping';
      confidence = 0.9;
      const total = this.getFormattedTotal();
      summary = `${this.items.length} items${total ? ` • ${total}` : ''}`;
      suggestedActions = [
        { label: 'Checkout', value: 'checkout', variant: 'primary' },
      ];
      if (this.items.length >= 2) {
        suggestedActions.push({ label: 'Compare Prices', value: 'compare_prices' });
      }
    }
    // Vehicle context
    else if (types.includes('vehicle')) {
      context = 'booking';
      confidence = 0.85;
      summary = `${this.items.length} vehicle${this.items.length > 1 ? 's' : ''} shortlisted`;
      suggestedActions = [
        { label: 'Book Test Drive', value: 'book_test_drive', variant: 'primary' },
        { label: 'Get Finance Quote', value: 'finance_quote' },
        { label: 'Compare Specs', value: 'compare_specs' },
      ];
    }
    // Property context
    else if (types.includes('property')) {
      context = 'booking';
      confidence = 0.85;
      summary = `${this.items.length} propert${this.items.length > 1 ? 'ies' : 'y'} saved`;
      suggestedActions = [
        { label: 'Schedule Viewings', value: 'schedule_viewings', variant: 'primary' },
        { label: 'Request Valuations', value: 'request_valuations' },
        { label: 'Compare Properties', value: 'compare_properties' },
      ];
    }
    // Player/recruitment context
    else if (types.includes('player') || categories.some(c => c?.toLowerCase().includes('player'))) {
      context = 'shortlist';
      confidence = 0.85;
      summary = `${this.items.length} player${this.items.length > 1 ? 's' : ''} shortlisted`;
      suggestedActions = [
        { label: 'Compare Players', value: 'compare_players', variant: 'primary' },
        { label: 'Contact Agents', value: 'contact_agents' },
        { label: 'Export Shortlist', value: 'export_shortlist' },
      ];
    }
    // Service context
    else if (types.includes('service')) {
      context = 'booking';
      confidence = 0.8;
      summary = `${this.items.length} service${this.items.length > 1 ? 's' : ''} selected`;
      suggestedActions = [
        { label: 'Book Services', value: 'book_services', variant: 'primary' },
        { label: 'Get Quote', value: 'get_quote' },
      ];
    }
    // Comparison context (multiple similar items)
    else if (this.items.length >= 2 && uniqueCategories.length === 1) {
      context = 'comparison';
      confidence = 0.75;
      summary = `Comparing ${this.items.length} ${uniqueCategories[0] || 'items'}`;
      suggestedActions = [
        { label: 'Compare Side by Side', value: 'compare_side_by_side', variant: 'primary' },
        { label: 'Find Best Option', value: 'find_best' },
        { label: 'Search Similar', value: 'search_similar' },
      ];
    }
    // Generic/research context
    else {
      context = 'research';
      confidence = 0.6;
      summary = `${this.items.length} item${this.items.length > 1 ? 's' : ''} collected`;
      suggestedActions = [
        { label: 'Search Similar', value: 'search_similar' },
      ];
      if (this.items.length >= 2) {
        suggestedActions.unshift({ label: 'Compare Items', value: 'compare_items', variant: 'primary' });
      }
      if (categories.length > 0) {
        suggestedActions.push({ label: `Browse More ${uniqueCategories[0] || ''}`, value: `browse_${uniqueCategories[0] || 'items'}` });
      }
    }

    return { type: context, confidence, suggestedActions, summary };
  }

  /**
   * Get unique categories from items.
   */
  getCategories(): string[] {
    return [...new Set(this.items.map(i => i.category).filter(Boolean) as string[])];
  }

  /**
   * Get items by category.
   */
  getItemsByCategory(category: string): ListItem[] {
    return this.items.filter(i => i.category === category);
  }
}

// Singleton instance for widget-wide state
let globalStore: SharedListStore | null = null;

/**
 * Get or create the global list store instance.
 */
export function getListStore(): SharedListStore {
  if (!globalStore) {
    globalStore = new SharedListStore();
  }
  return globalStore;
}

/**
 * Reset the global list store (for testing or demo switching).
 */
export function resetListStore(): void {
  globalStore?.clear();
  globalStore = null;
}
